/**
 * 미션 녹음 API — 서버 /api/sleep-care/mission/transcribe 호출
 * 서버는 multer 없이 express.raw()로 수신하므로 blob을 그대로 body에 전송
 */

import { supabase, supabaseAnonKey, supabaseUrl } from './supabaseClient';

const API_BASE = (process.env.REACT_APP_SOMNI_API_BASE_URL || '').replace(/\/$/, '');

const LISTEN_MIN_SECONDS = 0.25;
const TABLE_RELAXATION_LISTEN = 'cohort_relaxation_listen_events';

/**
 * 이완 훈련 주차 오디오 청취 구간(재생~일시정지/끝/탭 닫힘) 기록
 * @param {object} p
 * @param {string} p.cohortRelaxationWeeksId — public.cohort_relaxation_weeks.id
 * @param {number} p.secondsListened
 * @param {string} p.endReason — pause | ended | context_change | pagehide
 * @param {{ keepalive?: boolean }} [opts] — `pagehide` 직전 등 전송 취소 방지
 */
export async function insertRelaxationListenEvent(
  { cohortRelaxationWeeksId, secondsListened, endReason },
  opts = {}
) {
  if (!cohortRelaxationWeeksId || typeof cohortRelaxationWeeksId !== 'string') {
    return { ok: false };
  }
  const s = Math.min(Number(secondsListened) || 0, 86400);
  if (s < LISTEN_MIN_SECONDS) return { ok: false, skipped: true };

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) return { ok: false, skipped: true };

  const row = {
    user_id: session.user.id,
    cohort_relaxation_weeks_id: cohortRelaxationWeeksId,
    seconds_listened: Math.round(s * 100) / 100,
    end_reason: endReason,
  };

  if (opts.keepalive) {
    const u = new URL(supabaseUrl);
    u.pathname = `/rest/v1/${TABLE_RELAXATION_LISTEN}`;
    const r = await fetch(u.toString(), {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${session.access_token}`,
        Prefer: 'return=minimal',
      },
      body: JSON.stringify([row]),
    });
    if (!r.ok) {
      const t = await r.text().catch(() => '');
      console.error('[insertRelaxationListenEvent keepalive]', r.status, t.slice(0, 200));
    }
    return { ok: r.ok };
  }

  const { error } = await supabase.from(TABLE_RELAXATION_LISTEN).insert(row);
  if (error) {
    console.error('[insertRelaxationListenEvent]', error);
    return { ok: false, error };
  }
  return { ok: true };
}

/**
 * 녹음 음성을 서버로 전송 → R2 저장 + CLOVA STT → 텍스트 반환
 * @param {Blob} audioBlob
 * @param {string} dateKey
 * @param {string} accessToken Supabase access token
 */
export async function transcribeAudio(audioBlob, dateKey, accessToken) {
  const res = await fetch(`${API_BASE}/api/sleep-care/mission/transcribe`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': audioBlob.type || 'audio/webm',
      'X-Date-Key': dateKey,
    },
    body: audioBlob,
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || '음성 변환에 실패했습니다.');
  }
  return data;
}

/**
 * AI 슬립코치(SELF 코호트) 응답 요청
 * 서버가 Dify 호출 후 group_chat_messages에 코치 ID로 insert함.
 * 클라이언트는 Realtime 구독으로 새 메시지를 자동 수신.
 * @param {object} p
 * @param {string} p.userText  공유한 셀프 피드백 텍스트
 * @param {string} p.dateKey   YYYY-MM-DD
 * @param {string} p.cohortCareOptionId
 * @param {string} p.accessToken Supabase access token
 * @param {number} [p.timeoutMs] 클라이언트 타임아웃 (기본 30s)
 */
export async function requestCoachReply({
  userText,
  dateKey,
  cohortCareOptionId,
  parentMessageId,
  accessToken,
  timeoutMs = 30000,
}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE}/api/sleep-care/coach/respond`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userText,
        dateKey,
        cohortCareOptionId,
        parentMessageId: parentMessageId || null,
      }),
      signal: ctrl.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || 'AI 슬립코치 응답에 실패했습니다.');
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}
